import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import DomainBaseEntities from "@/utils/constants/DomainBaseEntities.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import GenericValues from "@/utils/common/GenericValues.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import CommonConstants from "@/utils/constants/CommonConstants.ts";
import ReportLauncherPlace from "@/utils/presenters/ReportLauncherPlace.ts";
import ReportViewerPlace from "@/utils/presenters/ReportViewerPlace.ts";
import CustomRepLaunchType from "@/utils/constants/CustomRepLaunchType.ts";
import EmbeddedViewUtils from "@/utils/editView/editScreens/EmbeddedViewUtils.ts";
import {QuestionFieldsSerializer} from "@/utils/urlUtils/CriteriaAndQuestionsURLParser.ts";
import router from "@/router";
import RouteNames from "@/router/RouteNames.ts";
import OutputFormat from "@/utils/constants/OutputFormat.ts";
import PrintingOption from "@/utils/constants/PrintingOption.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import NamaPrinting from "@/utils/common/NamaPrinting.ts";
import ReportViewerPresenter from "@/utils/presenters/ReportViewerPresenter.ts";
import {Dialog} from "quasar";
import ReportDialog from "@/components/dialogs/ReportDialog.vue";
import CustomRepInfo = namaTypes.CustomRepInfo;
import ByIdRequest = namaTypes.ByIdRequest;
import ReportMetadataContainer = namaTypes.ReportMetadataContainer;
import GenericValue = namaTypes.GenericValue;
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import ReportMetadata = namaTypes.ReportMetadata;
import LaunchReportRequest = namaTypes.LaunchReportRequest;
import PrintingParams = namaTypes.PrintingParams;

export default class CustomReportLauncherAction {
  presenter: WidgetsPresenter;
  customRepInfo: CustomRepInfo;

  constructor(presenter: WidgetsPresenter, customRepInfo: CustomRepInfo) {
    this.presenter = presenter;
    this.customRepInfo = customRepInfo;
  }

  get executeFn(): Function | undefined {
    return async () => {
      let request: ByIdRequest = {
        entityType: DomainBaseEntities.ReportDefinition,
        id: this.customRepInfo.reportId
      };
      let result = await NamaCommon.namaFetch(NamaURLS.FetchReportMetadata, {
        data: request
      });
      if (!result || !NamaCommon.isSuccess(result))
        return;
      let reportMetaData: ReportMetadataContainer = result.data as ReportMetadataContainer;
      if (this.presenter instanceof ListPresenter && reportMetaData.report?.extraData &&
        ObjectChecker.isAnyEqualToFirst(reportMetaData.report?.extraData["reportType"], "UserForm", "UserList")) {
        this.presenter.printUsing(this.customRepInfo);
        return;
      }
      if (this.presenter instanceof EditPresenter) {
        await this.handleCustomReportForEditView(reportMetaData);
      }
      if (this.presenter instanceof ListPresenter) {
        await CustomReportLauncherAction.handleCustomReportForListView(this.presenter, this.customRepInfo, reportMetaData);
      }
    };
  }

  private async handleCustomReportForEditView(reportMetaData: namaTypes.ReportMetadataContainer) {
    let parameterValues = this.presenter.fetchDispatcher().mapParameters(this.customRepInfo);
    let questions = CustomReportLauncherAction.buildQuestions(parameterValues, this.customRepInfo);
    await CustomReportLauncherAction.launchReport(questions, reportMetaData.metadata!, this.customRepInfo);
  }

  static async handleCustomReportForListView(presenter: ListPresenter, customRepInfo: CustomRepInfo, reportMetaData: ReportMetadataContainer) {
    let objects: BaseEntityDTO[];
    if (customRepInfo.selectedRecordsOnly)
      objects = AGGridUtils.fetchSelectedRows(presenter.gridApi.value!);
    else
      objects = AGGridUtils.getCurrentPageData(presenter.gridApi.value!);
    let list: string[] = [];
    for (let object of objects) {
      let copy = NamaCommon.cloneObject(object);
      list.push(FieldTypesUtils.toString(FieldType.Reference, copy));
    }
    let idsValue = GenericValues.createGenericValueBasedOnTheValue("ids",
      StringUtils.toCSVLineWithSep(CommonConstants.LIST_PARAMS_SEP, list));
    let entityTypeValue = GenericValues.createGenericValueBasedOnTheValue("entityType", presenter.entityType);
    let mappedFields = EmbeddedViewUtils.collectFieldsFromParameterMapping(customRepInfo);
    if (customRepInfo.combineInOneReportWhenPrintingFromListView) {
      if(objects.length === 0)
        return;
      let parameterValues: Record<string, GenericValue> = {};
      parameterValues["ids"] = idsValue;
      parameterValues["$this"] = idsValue;
      parameterValues["entityType"] = entityTypeValue;
      for (let field of mappedFields) {
        if (parameterValues[field])
          continue;
        if (objects.length > 0 && objects[0][field] !== undefined)
          parameterValues[field] = GenericValues.createGenericValueBasedOnTheValue(field, objects[0][field]);
      }
      let questions = CustomReportLauncherAction.buildQuestions(parameterValues, customRepInfo);
      await CustomReportLauncherAction.launchReport(questions, reportMetaData.metadata!, customRepInfo);
    } else {
      for (let object of objects) {
        let parameterValues: Record<string, GenericValue> = {};
        parameterValues["ids"] = idsValue;
        parameterValues["$this"] = GenericValues.createGenericValueBasedOnTheValue("$this",
          FieldTypesUtils.toString(FieldType.Reference, NamaCommon.cloneObject(object)));
        parameterValues["entityType"] = entityTypeValue;
        for (let field of mappedFields) {
          if (parameterValues[field])
            continue;
          if (object[field] !== undefined)
            parameterValues[field] = GenericValues.createGenericValueBasedOnTheValue(field, object[field]);
        }
        let questions = CustomReportLauncherAction.buildQuestions(parameterValues, customRepInfo);
        await CustomReportLauncherAction.launchReport(questions, reportMetaData.metadata!, customRepInfo);
      }
    }
  }

  static buildQuestions(parameterValues: Record<string, GenericValue>, customRepInfo: CustomRepInfo): DTOFilledQuestionField[] {
    let parameterFieldMap = EmbeddedViewUtils.parameterFieldMap(customRepInfo);
    let questions: DTOFilledQuestionField[] = [];
    for (let key in parameterFieldMap) {
      let value = parameterValues[parameterFieldMap[key]];
      if (!value)
        continue;
      let realValue = GenericValues.getValue(value);
      questions.push({
        id: key,
        value: FieldTypesUtils.toString(value.dataType, realValue),
        objValue: realValue
      });
    }
    return questions;
  }

  static async launchReport(questions: DTOFilledQuestionField[], metaData: ReportMetadata, customRepInfo: CustomRepInfo) {
    let place: ReportLauncherPlace | ReportViewerPlace | undefined;
    switch (customRepInfo.launchType) {
      case CustomRepLaunchType.DirectInNewPage:
      case CustomRepLaunchType.DirectInPopup:
      case CustomRepLaunchType.Print:
        place = CustomReportLauncherAction.createReportViewerPlace(questions, metaData, customRepInfo);
        break;
      case CustomRepLaunchType.ParamsInNewPage:
      case CustomRepLaunchType.ParamsInPopup:
        place = new ReportLauncherPlace();
        break;
      default:
        place = CustomReportLauncherAction.createReportViewerPlace(questions, metaData, customRepInfo);
        break;
    }
    this.appendCommonDataToReportPlace(place, questions, customRepInfo);
    switch (customRepInfo.launchType) {
      case CustomRepLaunchType.DirectInNewPage:
      case CustomRepLaunchType.ParamsInNewPage:
        let resolve = router.resolve({
          name: RouteNames.REPORT_RESULT_ROUTE,
          query: place.fetchQuery()
        });
        window.open(resolve.href, "_blank");
        break;
      case CustomRepLaunchType.Print:
        await CustomReportLauncherAction.printReport(place as ReportViewerPlace);
        break;
      case CustomRepLaunchType.DirectInPopup:
      case CustomRepLaunchType.ParamsInPopup:
        place = new ReportLauncherPlace();
        CustomReportLauncherAction.appendCommonDataToReportPlace(place, questions, customRepInfo);
        await CustomReportLauncherAction.presentReportInDialog(place, metaData, customRepInfo);
        break;
    }
  }

  private static async presentReportInDialog(place: ReportLauncherPlace, metaData: ReportMetadata, customRepInfo: CustomRepInfo) {
    Dialog.create({
      component: ReportDialog,
      componentProps: {
        reportInfo: customRepInfo,
        place: place
      }
    })
  }

  private static createReportViewerPlace(questions: namaTypes.DTOFilledQuestionField[], metaData: namaTypes.ReportMetadata | undefined, customRepInfo: CustomRepInfo) {
    let place = new ReportViewerPlace();
    EmbeddedViewUtils.addDefaultValueQuestions(questions, metaData);
    this.appendCommonDataToReportPlace(place, questions, customRepInfo);
    return place;
  }

  private static appendCommonDataToReportPlace(place: ReportLauncherPlace | ReportViewerPlace, questions: DTOFilledQuestionField[], customRepInfo: CustomRepInfo) {
    place.questions = new QuestionFieldsSerializer().toURL(questions);
    place.reportId = customRepInfo.reportId;
  }

  private static async printReport(place: ReportViewerPlace) {
    let request: LaunchReportRequest = {
      questions: place.questions,
      reportId: place.reportId,
      outputFormat: OutputFormat.PDF,
      printingOption: PrintingOption.DIRECT,
      downloadPurpose: DownloadPurpose.PRINT
    }
    let result = await NamaCommon.namaFetch(NamaURLS.RunReport, {
      data: request
    });
    if (NamaCommon.isSuccess(result) && result.list && result.list[0]) {
      let params: PrintingParams = result.list[0];
      if (ClientCache.shouldPrintUsingNamaServer()) {
        await NamaPrinting.printUsingPrintingServer([params]);
      } else {
        let url = ReportViewerPresenter.rptUrl(place.reportId, params.tempPrintId ?? '', place.questions!, OutputFormat.PDF,
          NamaPrinting.getDirectPrintOption(), DownloadPurpose.PRINT, params.printerName ?? '');
        NamaPrinting.printNext([url], 0);//TODO: fix this
      }
    }
  }
}
